import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PaperPlaneRight, Sparkle, Receipt, CircleNotch, ChatCircleDots } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";
import { catColor } from "@/components/riq";
import { MerchantLogo } from "@/components/riq";

const SUGGESTED = [
  "How much did I spend this month?",
  "What are my biggest spending categories?",
  "When did I last buy coffee?",
  "Find my Checkers receipts.",
  "What items do I buy most often?",
  "Has the price of coffee increased?",
];

export default function AskAI() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cur = user?.currency || "ZAR";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/query", { question: q });
      setMessages((m) => [...m, { role: "ai", text: data.answer, receipts: data.receipts || [] }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't process that right now. Please try again.", receipts: [] }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-11rem-env(safe-area-inset-bottom))] lg:h-[calc(100vh-6rem)] max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl bg-riq-primary flex items-center justify-center relative overflow-hidden"><div className="absolute inset-0 opacity-20 riq-noise" /><Sparkle size={22} weight="fill" className="text-riq-gold relative" /></div>
        <div><h1 className="font-head text-2xl font-bold tracking-tight text-riq-primary">Ask AI</h1><p className="text-sm text-muted-foreground">Ask anything about your purchases.</p></div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {messages.length === 0 && (
          <div className="py-6">
            <div className="rounded-2xl bg-secondary/60 border border-riq-border p-5 mb-5">
              <p className="text-sm text-neutral-700 leading-relaxed">Hi {user?.name?.split(" ")[0] || "there"} 👋 I can answer questions about your spending, merchants, items, prices and budgets — all from your real receipt data. Try one of these:</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {SUGGESTED.map((s) => (
                <button key={s} data-testid={`suggested-${s.slice(0, 12)}`} onClick={() => send(s)} className="text-left rounded-xl border border-riq-border bg-white px-4 py-3 text-sm hover:border-riq-primary hover:-translate-y-0.5 transition-all">{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={m.role === "user" ? "flex justify-end" : ""}>
            {m.role === "user" ? (
              <div data-testid={`user-msg-${i}`} className="bg-riq-primary text-white rounded-2xl rounded-br-md px-4 py-3 text-sm max-w-[80%]">{m.text}</div>
            ) : (
              <div className="max-w-[90%]">
                <div className="flex items-center gap-2 mb-1.5 text-xs text-muted-foreground"><Sparkle size={14} weight="fill" className="text-riq-gold" /> Sliply</div>
                <div data-testid={`ai-msg-${i}`} className="bg-secondary rounded-2xl rounded-tl-md px-4 py-3 text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap">{m.text}</div>
                {m.receipts?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {m.receipts.map((r) => (
                      <button key={r.id} data-testid={`ai-receipt-${r.id}`} onClick={() => navigate(`/receipts/${r.id}`)} className="w-full flex items-center gap-3 rounded-xl border border-riq-border bg-white px-4 py-3 text-left hover:border-riq-primary transition-colors">
                        <MerchantLogo name={r.merchant_name} size={36} />
                        <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate">{r.merchant_name}</div><div className="text-xs text-muted-foreground">{shortDate(r.date)}{r.matched_item ? ` · ${r.matched_item}` : ""}</div></div>
                        <span className="font-head font-semibold text-sm">{money(r.total, cur)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ))}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><CircleNotch size={16} className="animate-spin" /> Thinking through your data…</div>
        )}
        <div ref={endRef} />
      </div>

      <div className="pt-3 border-t border-riq-border">
        <div className="flex items-center gap-2 bg-white rounded-full border border-riq-border pl-5 pr-2 py-2 focus-within:border-riq-primary">
          <input data-testid="ask-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your spending…" className="flex-1 bg-transparent text-sm outline-none" />
          <button data-testid="ask-send-btn" onClick={() => send()} disabled={loading || !input.trim()} className="w-10 h-10 rounded-full bg-riq-primary text-white flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform"><PaperPlaneRight size={18} weight="fill" /></button>
        </div>
      </div>
    </div>
  );
}
